import { dialogOpened } from 'Core/actions/dialog';
import {
  addToGarageRequested,
  removalFromGarageRequested,
  sendVerifyFitmentRequest,
  setFitmentTableResponse,
  setGarageRequested,
  vehicleDiscardingRequested,
  vehicleSelected,
} from 'Core/actions/fitmentSearch/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchFieldsSelector,
  garageDataSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
  vehicleSelectionSelector,
  verifyFitmentProductSelector,
} from 'Core/selectors/fitmentSearch/index';
import type { ReduxStateType, ReduxStoreType } from 'Core/store';
import { Vehicle, VehicleCollection } from 'Models/index';
import productConfig from 'Models/uiConfig/productConfig';
import type { VehicleFieldMap } from 'Models/vehicle';
import { queryParamsFromObject } from 'Modules/converter';
import { facetsToConvertible } from 'Modules/serverApi/common/convert';
import { fitments, search } from 'Modules/serverApi/index';
import type { ServerModel, VerifyFitsValue } from 'Modules/serverApi/types';
import {
  type AutoSyncPartLoader,
  type AutoSyncVisualizer,
  createAutoSyncPartLoader,
  visualizeVehicle,
  type VisualizeVehicleParams,
} from 'Utils/autoSync';
import { isObjectRecord } from 'Utils/typeGuards';

type FieldMap = VehicleFieldMap;

export type FitmentSearchAPI = {
  isVehicleSelected: () => boolean;
  getVehicle: () => FieldMap;
  getVehicleString: () => string;
  getVehicleSearchParams: () => string;
  getVehicleRequestParams: () => string;
  setVehicle: (vehicle: FieldMap, sendIsolatedRequest?: boolean) => void;
  discardVehicle: () => void;
  addVehicle: (vehicle: FieldMap) => void;
  removeVehicle: (vehicle: FieldMap) => void;
  getVehicleList: () => FieldMap[];
  setVehicleList: (vehicles: FieldMap[]) => void;
  addVehicleList: (vehicles: FieldMap[]) => void;
  verifyFitmentAsync: (
    productId?: string | FieldMap,
    vehicle?: FieldMap,
  ) => Promise<VerifyFitsValue | undefined>;
  updateVerifyFitmentWidget: (productId: number, variantId?: number) => void;
  updateFitmentTableWidget: (productId: string, variantId?: string) => Promise<void>;
  openVehicleDialog: () => void;
  createAutoSyncPartLoader: (isTire?: boolean, useAutoSyncId?: boolean) => AutoSyncPartLoader;
  autoSyncVisualizeVehicle: (params: VisualizeVehicleParams) => AutoSyncVisualizer | undefined;
};

export default function getFitmentSearchAPI(store: ReduxStoreType): FitmentSearchAPI {
  const { dispatch } = store;

  return {
    isVehicleSelected: () => isVehicleSelectedSelector(store.getState()),
    getVehicle: () => selectedVehicleSelector(store.getState()).filteredFieldMap,
    getVehicleString: () => selectedVehicleSelector(store.getState()).toString(),
    getVehicleSearchParams: () => {
      const selection = vehicleSelectionSelector(store.getState());
      return selection.map((s) => `${s.field}/${s.term}`).join('/');
    },
    getVehicleRequestParams: () => {
      const selection = vehicleSelectionSelector(store.getState());

      if (!selection.length) {
        console.warn('Please, select a vehicle first.');
        return '';
      }

      return queryParamsFromObject({
        facet: facetsToConvertible({ maxCountByField: {}, selection }),
      });
    },
    setVehicle: (vehicle, sendIsolatedRequest = true) => {
      const state = store.getState();

      if (!isVehicleHasAllRequiredFields(state, Object.keys(vehicle))) {
        throwIncompleteVehicleError(state);
      }

      dispatch(
        vehicleSelected(new Vehicle(vehicle, fitmentSearchFieldsSelector(state)), {
          sendIsolatedRequest,
        }),
      );
    },
    discardVehicle: () => {
      dispatch(vehicleDiscardingRequested({ mayDiscardValue: false }));
    },
    addVehicle: (vehicle) => {
      const state = store.getState();

      if (!isVehicleHasAllRequiredFields(state, Object.keys(vehicle))) {
        throwIncompleteVehicleError(state);
      }

      dispatch(addToGarageRequested(new Vehicle(vehicle, fitmentSearchFieldsSelector(state))));
    },
    removeVehicle: (vehicle) => {
      const state = store.getState();

      const vehicleToRemove = new Vehicle(vehicle, fitmentSearchFieldsSelector(state));
      const selectedVehicle = selectedVehicleSelector(state);

      dispatch(removalFromGarageRequested(vehicleToRemove, vehicleToRemove.equals(selectedVehicle)));
    },
    getVehicleList: () => garageDataSelector(store.getState()).map((vehicle) => vehicle.filteredFieldMap),
    setVehicleList: (vehicles) => {
      const state = store.getState();

      if (vehicles.some((vehicle) => !isVehicleHasAllRequiredFields(state, Object.keys(vehicle)))) {
        throwIncompleteVehicleError(state);
      }

      dispatch(
        setGarageRequested({
          vehicles: new VehicleCollection(
            vehicles.map((vehicle) => new Vehicle(vehicle, fitmentSearchFieldsSelector(state))),
          ),
          selectVehicle: true,
        }),
      );
    },
    addVehicleList: (vehicles) => {
      const state = store.getState();

      if (vehicles.some((vehicle) => !isVehicleHasAllRequiredFields(state, Object.keys(vehicle)))) {
        throwIncompleteVehicleError(state);
      }

      const currentVehicles = garageDataSelector(state);
      const newVehicles = new VehicleCollection(
        vehicles.map((vehicle) => new Vehicle(vehicle, fitmentSearchFieldsSelector(state))),
      );

      const combinedVehicles = currentVehicles.concat(newVehicles);

      if (combinedVehicles.size > currentVehicles.size) {
        dispatch(setGarageRequested({ vehicles: combinedVehicles, selectVehicle: false }));
      }
    },
    verifyFitmentAsync: (productId, vehicle) => {
      const state = store.getState();

      // only vehicle provided
      if (isFieldMap(productId) && !vehicle) {
        return verifyFitmentAsyncInternal(state, undefined, productId);
      }

      if (productId && typeof productId !== 'string') {
        throw Error('Provided product id must be a string value');
      }

      return verifyFitmentAsyncInternal(state, productId, vehicle);
    },
    updateVerifyFitmentWidget: (productId: number, variantId?: number) => {
      if (productId) {
        const state = store.getState();
        const selectedVehicle = selectedVehicleSelector(state);

        dispatch(sendVerifyFitmentRequest({ productId, vehicle: selectedVehicle, variantId }));
      } else {
        throw new Error('Provided product id not found');
      }
    },
    openVehicleDialog: () => {
      dispatch(dialogOpened('VehicleWidgetDialog', { withCurrentVehicle: true }));
    },
    updateFitmentTableWidget: async (productId: string, variantId?: string) => {
      if (!productId) {
        throw new Error('Product id is required for update fitment table widget');
      }

      try {
        const productFitments = await getFitmentsAsyncInternal(productId, variantId);
        dispatch(setFitmentTableResponse(productFitments));
      } catch (error) {
        throw new Error('Provided product id not found', { cause: error });
      }
    },
    createAutoSyncPartLoader,
    autoSyncVisualizeVehicle: visualizeVehicle,
  };
}

function isVehicleHasAllRequiredFields(state: ReduxStateType, fields: string[]): boolean {
  const baseFitmentSearchFields = fitmentSearchBaseFieldsSelector(state);
  return baseFitmentSearchFields.every((field) => fields.includes(field));
}

function throwIncompleteVehicleError(state: ReduxStateType): never {
  const baseFitmentSearchFields = fitmentSearchBaseFieldsSelector(state);
  throw new Error(`Vehicle must have all required fields: ${baseFitmentSearchFields.join(', ')}.`);
}

async function getFitmentsAsyncInternal(
  productId: string,
  variantId?: string,
): Promise<ServerModel.FitmentsResponse> {
  return fitments(productId, variantId);
}

async function verifyFitmentAsyncInternal(
  state: ReduxStateType,
  productId: string = productConfig.localItemId,
  vehicle?: FieldMap,
): Promise<VerifyFitsValue | undefined> {
  const selectedVehicle = selectedVehicleSelector(state);

  const vehicleToVerify = vehicle
    ? new Vehicle(vehicle, fitmentSearchFieldsSelector(state))
    : selectedVehicle;

  if (vehicleToVerify === Vehicle.null || !productId) {
    let message = '';

    if (!productId) {
      message +=
        'Function must be used on the product page or product id must be provided as the first function parameter. ';
    }

    if (vehicleToVerify === Vehicle.null) {
      message += `Vehicle must be selected on the page or provided as the second function parameter with all required fields: ${fitmentSearchBaseFieldsSelector(
        state,
      ).join(', ')}.`;
    }

    throw new Error(message);
  }

  const productData = verifyFitmentProductSelector(state);
  const fitsTheVehicle = productData?.fitsTheVehicle;

  if (productId === productData?.id && fitsTheVehicle && vehicleToVerify.equals(selectedVehicle)) {
    return fitsTheVehicle;
  }

  const request = {
    selection: vehicleToVerify.selection,
    pageSize: 1,
    mode: 'VerifyFitment',
    filterQuery: `${productConfig.fields.id}:${productId}`,
  };
  return search(request).then((res) => {
    const [item] = res.Items;

    if (!item) {
      throw new Error(
        `No product found with id: '${productId}'. Please check if product id is correct or start reindexing.`,
      );
    }

    const { fitsTheVehicle } = item;

    if (fitsTheVehicle) {
      return fitsTheVehicle;
    }

    throw new Error('Something went wrong. If this error persists, please notify Convermax.');
  });
}

function isFieldMap(value: unknown): value is FieldMap {
  return isObjectRecord(value) && Object.values(value).every((entry) => typeof entry === 'string');
}
